import { Observable } from 'rxjs';

import { TrackingDevice } from './tracking-device.model';
import { ImmutableMap, Prop, QueryParams, RequiredOnly, isObject, isString } from './utility.model';

export const USER_ROLES = ['admin', 'owner', 'member'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_LANGS = ['ja', 'en-GB', 'en-US', 'de', 'tl', 'th'] as const;
export type UserLang = (typeof USER_LANGS)[number];

export const PRIVACY_POLICY_URL = 'https://www.daikin.co.uk/en_gb/residential/data-protection-policy.html';

export const USER_TIMEZONES = [
  'America/Chicago',
  'America/New_York',
  'Europe/Dublin',
  'Europe/Berlin',
  'Asia/Kolkata',
  'Asia/Yangon',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Australia/Perth',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
] as const;
export type UserTimezone = (typeof USER_TIMEZONES)[number];

type UserRequiredProps = {
  email: string;
  organizationId: string;
  name: string;
  role: UserRole;
  lang: UserLang;
  tz: UserTimezone;
};

type UserOptionalProps = {
  phoneNumber?: string;
  countryCode?: string;
  remarks?: string;
};

type UserProvidedProps = {
  userId: string;
  enabled: boolean;
  mfa?: boolean;
  updatedAt: number;
  consentedAt: number;
  works: string[];
  working: string;
  version: number;
};

export type UserQueryParams = QueryParams & { organizationId?: string };

export type UserCreateParams = UserRequiredProps & UserOptionalProps;

export type UserUpdateParams = Partial<Omit<UserRequiredProps, 'email' | 'organizationId'>> & UserOptionalProps;

export type User = UserRequiredProps &
  UserOptionalProps &
  UserProvidedProps & {
    trackingDevices$: Observable<TrackingDevice[]>;
  };

export function isUserCreateParams(value: unknown): value is UserCreateParams {
  const sProps: Prop<RequiredOnly<UserCreateParams>, string>[] = ['email', 'organizationId', 'name'];
  return (
    isObject(value) &&
    sProps.every(prop => isString(value[prop])) &&
    USER_ROLES.some(v => v === value['role']) &&
    USER_LANGS.some(v => v === value['lang']) &&
    USER_TIMEZONES.some(v => v === value['tz'])
  );
}

export function isUserUpdateParams(value: unknown): value is UserUpdateParams {
  const sProps: Prop<UserUpdateParams, string>[] = ['name', 'phoneNumber', 'countryCode', 'remarks'];
  return (
    isObject(value) &&
    (sProps.some(prop => isString(value[prop])) ||
      USER_ROLES.some(v => v === value['role']) ||
      USER_LANGS.some(v => v === value['lang']) ||
      USER_TIMEZONES.some(v => v === value['tz']))
  );
}

export class Users extends ImmutableMap<string, User> {
  constructor(users?: User[] | Iterable<User>) {
    super(user => user.userId, users);
  }

  protected clone(users?: User[] | Iterable<User>): this {
    return new Users(users) as this;
  }
}
